import type { AnyBlockConfigOnComponentOptions } from '#layers/block-engine/public';
import type { DisplayTemplateAst } from '#layers/data-model/types/display-template';
import type { FieldSelectionContext, IdFieldSelection } from '#layers/data-model/types/field-selection';
import type { M2MStructure } from '#layers/data-model/types/relation';
import type { GlobalCollectionId, GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model/types/schema/ids';
import type {
	AnyResolvedField,
	ResolvedMonospaceCollection,
	ResolvedMonospaceSingleRelationField,
} from '#layers/data-model/types/schema/resolved-schema';
import { callOrReturn } from '#layers/base/public';
import { RELATED_VALUES_DROPDOWN_MAX_ITEM_COUNT } from '#layers/data-model/constants/related-values';
import { DisplayTemplateAstType } from '#layers/data-model/types/display-template';
import { getM2MDefaultDisplayTemplate, isEmptyDisplayTemplate, resolveCollectionDisplayTemplate } from '#layers/data-model/utils/display-template';
import { mergeIdFieldSelections } from '#layers/data-model/utils/field-selection/merge-field-selection';
import { getFieldDisplayBlockOptions } from '#layers/data-model/utils/fields/field-display';
import { findCollectionFieldById } from '#layers/data-model/utils/fields/fields';
import { getM2MStructure, isM2M } from '#layers/data-model/utils/relation';
import { isPrimitiveField } from '#layers/data-model/utils/schema/collection';

export interface ResolvedFieldSelectionContext {
	getDisplayTemplateFieldSelection: (ast: DisplayTemplateAst, collection: ResolvedMonospaceCollection) => IdFieldSelection;
	getCollection: (id: GlobalCollectionId) => ResolvedMonospaceCollection | undefined;
	getRelationField: (fieldId: GlobalSingleRelationFieldId) => ResolvedMonospaceSingleRelationField | undefined;
	resolveDisplayBlock: (displayId: string) => Pick<AnyBlockConfigOnComponentOptions, 'display'> | undefined;
}

const MAX_DEPTH = 15;

function relationEntry(fieldId: GlobalSingleRelationFieldId, selection: IdFieldSelection): IdFieldSelection {
	return { fields: [], include: { [fieldId]: selection } };
}

/**
 * Resolve the IdFieldSelection needed to display a single field.
 *
 * For primitive fields: always includes the field's own ID, plus any extra
 * fields declared by the display block's `selection()` config.
 * For relation fields: checks the display block's `selection()` config, falling
 * back to the opposite collection's display template.
 */
export function resolveFieldDisplayFieldSelection(
	field: AnyResolvedField,
	collection: ResolvedMonospaceCollection,
	ctx: ResolvedFieldSelectionContext,
): IdFieldSelection {
	return resolveFieldDisplayFieldSelectionRecursive(field, collection, ctx, new Set());
}

function resolveFieldDisplayFieldSelectionRecursive(
	field: AnyResolvedField,
	collection: ResolvedMonospaceCollection,
	ctx: ResolvedFieldSelectionContext,
	_visited: Set<GlobalCollectionId>,
): IdFieldSelection {
	if (isPrimitiveField(field)) {
		let result: IdFieldSelection = { fields: [field.id as GlobalPrimitiveFieldId] };

		const { block, props: blockProps } = getFieldDisplayBlockOptions(field);
		const blockConfig = ctx.resolveDisplayBlock(block);
		const displaySelection = blockConfig?.display?.selection;

		if (displaySelection) {
			const extraSelection = callOrReturn(displaySelection, blockProps, ctx as FieldSelectionContext);
			const expanded = resolveDisplayIncludes(extraSelection, collection, ctx, _visited);
			result = mergeIdFieldSelections(result, expanded);
		}

		return result;
	}

	const relFieldId = field.id as GlobalSingleRelationFieldId;
	const rawSelection = getDisplayBlockFieldSelection(relFieldId, ctx);
	return resolveDisplayIncludes(rawSelection, collection, ctx, _visited);
}

export function getDisplayTemplateFieldSelection(
	ast: DisplayTemplateAst,
	collection: ResolvedMonospaceCollection,
	ctx: ResolvedFieldSelectionContext,
	_visited: Set<GlobalCollectionId> = new Set(),
): IdFieldSelection {
	if (!ast.content || ast.content.length === 0) return { fields: [] };
	if (_visited.size >= MAX_DEPTH) return { fields: ['*'] };

	let result: IdFieldSelection = { fields: [] };

	for (const node of ast.content) {
		if (node.type !== DisplayTemplateAstType.Ref) continue;

		const { fieldId } = node.attrs;

		// Array fieldId = pre-resolved path (e.g. [relId, nestedRelId, primitiveId])
		if (Array.isArray(fieldId)) {
			const selection = fieldIdPathToSelection(fieldId, collection, ctx, _visited);

			if (selection) {
				result = mergeIdFieldSelections(result, selection);
			}

			continue;
		}

		const field = findCollectionFieldById(collection, fieldId);

		if (!field) continue;

		result = mergeIdFieldSelections(result, resolveFieldDisplayFieldSelectionRecursive(field, collection, ctx, _visited));
	}

	return result;
}

/**
 * Get the field selection from a display block's selection() config.
 * Falls back to an empty relation query when no display block fields exist.
 */
function getDisplayBlockFieldSelection(
	relFieldId: GlobalSingleRelationFieldId,
	ctx: ResolvedFieldSelectionContext,
): IdFieldSelection {
	const field = ctx.getRelationField(relFieldId);

	if (!field) return relationEntry(relFieldId, {});

	const { block, props: blockProps } = getFieldDisplayBlockOptions(field);
	const blockConfig = ctx.resolveDisplayBlock(block);
	const displaySelection = blockConfig?.display?.selection;

	if (displaySelection) {
		const result = callOrReturn(displaySelection, blockProps, ctx as FieldSelectionContext);

		warnIfBareOppositeCollectionFields({ selection: result, relFieldId, field, blockId: block });

		return result;
	}

	// Default: an empty relation query triggers display-template expansion
	return relationEntry(relFieldId, {});
}

/**
 * Warn when a display block returns bare field IDs that likely belong to the opposite
 * collection instead of the current one. display.selection() must return selections relative
 * to the current collection; opposite-collection fields need to be nested under the relation.
 */
function warnIfBareOppositeCollectionFields(options: {
	selection: IdFieldSelection;
	relFieldId: GlobalSingleRelationFieldId;
	field: ResolvedMonospaceSingleRelationField;
	blockId: string;
}) {
	const { selection, relFieldId, field, blockId } = options;
	const bareStrings = (selection.fields ?? []).filter((entry) => typeof entry === 'string' && entry !== '*') as string[];

	if (bareStrings.length === 0) return;

	// Check if any bare strings are NOT primitives on the current collection
	const currentCollection = field.collection;

	if (!currentCollection) return;

	const currentPrimitiveIds = new Set(currentCollection.primitiveFields?.map((f) => f.id as string));
	const currentRelationIds = new Set(currentCollection.relationFields?.map((f) => f.id as string));
	const unknownFields = bareStrings.filter((id) => !currentPrimitiveIds.has(id) && !currentRelationIds.has(id));

	if (unknownFields.length > 0) {
		console.warn(
			`[display.selection] Block "${blockId}" returned bare field IDs that don't exist on the current collection: ${unknownFields.join(', ')}. `
			+ 'display.selection() must return selections relative to the current collection. '
			+ `Opposite-collection fields should be nested: { include: { "${relFieldId}": { fields: [...] } } }`,
		);
	}
}

/** Resolve an empty included query from the relation's display template. */
function resolveDisplayIncludes(
	selection: IdFieldSelection,
	collection: ResolvedMonospaceCollection,
	ctx: ResolvedFieldSelectionContext,
	_visited: Set<GlobalCollectionId>,
): IdFieldSelection {
	const result: IdFieldSelection = { ...selection, include: { ...selection.include } };

	for (const [key, query] of Object.entries(selection.include ?? {})) {
		if (query.fields !== undefined || query.include !== undefined) continue;
		const relation = collection.relationFields.find((field) => field.id === key);
		if (!relation) continue;
		const nested = isM2M(relation)
			? resolveM2MJunctionSelection(relation, collection.id, ctx, _visited)
			: resolveRelationFieldSelection(relation.oppositeCollectionId, collection.id, ctx, _visited);
		if (!nested) continue;

		result.include![key as GlobalSingleRelationFieldId] = {
			...nested,
			...(relation.isList ? { limit: RELATED_VALUES_DROPDOWN_MAX_ITEM_COUNT + 1 } : {}),
			...query,
		};
	}

	return result;
}

function resolveRelationFieldSelection(
	oppositeCollectionId: GlobalCollectionId,
	currentCollectionId: GlobalCollectionId,
	ctx: ResolvedFieldSelectionContext,
	_visited: Set<GlobalCollectionId>,
): IdFieldSelection {
	const oppositeCollection = ctx.getCollection(oppositeCollectionId);
	if (!oppositeCollection) return { fields: ['*'] };

	if (_visited.has(oppositeCollection.id)) return { fields: ['*'] };

	const oppositeDisplayTemplate = resolveCollectionDisplayTemplate(oppositeCollection);

	if (isEmptyDisplayTemplate(oppositeDisplayTemplate)) {
		return { fields: ['*'] };
	}

	const nextVisited = new Set(_visited);
	nextVisited.add(currentCollectionId);

	return getDisplayTemplateFieldSelection(
		oppositeDisplayTemplate,
		oppositeCollection,
		ctx,
		nextVisited,
	);
}

function resolveM2MJunctionSelection(
	field: ResolvedMonospaceSingleRelationField,
	sourceCollectionId: GlobalCollectionId,
	ctx: ResolvedFieldSelectionContext,
	_visited: Set<GlobalCollectionId>,
): IdFieldSelection | null {
	let structure: M2MStructure;

	try {
		structure = getM2MStructure(field);
	}
	catch {
		return null;
	}

	const junctionCollection = ctx.getCollection(structure.junction.collectionId);
	if (!junctionCollection) return null;

	// CYCLE GUARD: descent is source → junction → (toTarget) → target. Seed both the source
	// and the junction so a target template referencing the inverse M2M terminates instead of
	// recursing to MAX_DEPTH.
	const visited = new Set(_visited);
	visited.add(sourceCollectionId);
	visited.add(structure.junction.collectionId);

	return getDisplayTemplateFieldSelection(
		getM2MDefaultDisplayTemplate(structure),
		junctionCollection,
		ctx,
		visited,
	);
}

function fieldIdPathToSelection(
	fieldIds: GlobalFieldId[],
	collection: ResolvedMonospaceCollection,
	ctx: ResolvedFieldSelectionContext,
	_visited: Set<GlobalCollectionId>,
): IdFieldSelection | null {
	const [fieldId, ...rest] = fieldIds;
	if (!fieldId) return null;

	const field = findCollectionFieldById(collection, fieldId);
	if (!field) return null;

	if (isPrimitiveField(field)) {
		return rest.length === 0 ? { fields: [field.id] } : null;
	}

	const relFieldId = field.id as GlobalSingleRelationFieldId;

	if (rest.length === 0) {
		return resolveFieldDisplayFieldSelectionRecursive(field, collection, ctx, _visited);
	}

	const oppositeCollection = field.oppositeCollection ?? ctx.getCollection(field.oppositeCollectionId);
	if (!oppositeCollection) return null;

	const nestedSelection = fieldIdPathToSelection(rest, oppositeCollection, ctx, _visited);
	if (!nestedSelection) return null;

	return relationEntry(relFieldId, nestedSelection);
}

export function resolveCollectionDisplayTemplateSelection(
	collection: ResolvedMonospaceCollection,
	ctx: ResolvedFieldSelectionContext,
	displayTemplate?: DisplayTemplateAst,
): IdFieldSelection {
	const ast = displayTemplate ?? resolveCollectionDisplayTemplate(collection);

	if (isEmptyDisplayTemplate(ast)) return { fields: ['*'] };

	return getDisplayTemplateFieldSelection(ast, collection, ctx);
}

/**
 * Resolve the `IdFieldSelection` for one display-template field-path ref, so
 * callers can memoize query fields per visible path.
 */
export function resolveFieldPathSelection(
	collection: ResolvedMonospaceCollection,
	fieldPath: GlobalFieldId[],
	ctx: ResolvedFieldSelectionContext,
): IdFieldSelection {
	if (fieldPath.length === 0) return { fields: [] };

	if (fieldPath.length > 1) {
		const selection = fieldIdPathToSelection(fieldPath, collection, ctx, new Set());
		return selection ?? { fields: [] };
	}

	const field = findCollectionFieldById(collection, fieldPath[0]!);
	if (!field) return { fields: [] };

	return resolveFieldDisplayFieldSelectionRecursive(field, collection, ctx, new Set());
}
